import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from 'lib/httpClient';
import downloadFile from 'utils/downloadFile';
import { ApiResponse } from 'further-types/api';
import { Api } from 'further-types/allocations';
import { useNotification } from 'hooks/ui/useNotification';
import { CalendarDay, datedFilename } from 'further-ui/utils';

// Download template file function
async function downloadTemplateFile(formData: Api.DownloadTemplateFormData) {
  const response = await api.post(
    'allocations/download-share-allocation-template-file',
    formData,
    {
      responseType: 'blob',
      validateStatus: (status) => status === 200,
    },
  );

  downloadFile(response.data, `Allocation-upload-sheet-${Date.now()}.xlsx`);

  return response.data;
}

// Upload Excel file function
async function uploadAllocationsFile(formData: FormData) {
  const response = await api.post<
    ApiResponse<Api.ProcessShareAllocationsResponse>
  >('allocations/process-share-allocations-from-file', formData);

  response.data.data?.allocationPreviews.forEach((preview) => {
    preview.allocationDate = new CalendarDay(String(preview.allocationDate));
  });

  return response.data;
}

// Export to custodian function
async function exportToCustodian(params: {
  shareAllocationUploadId: string;
  companyTradingName?: string;
  allocationDate?: Date | string;
}) {
  const { shareAllocationUploadId, companyTradingName, allocationDate } =
    params;
  const response = await api.get(
    `allocations/${shareAllocationUploadId}/export-to-custodian`,
    {
      responseType: 'blob',
      validateStatus: (status) => status === 200,
    },
  );

  const date = allocationDate ? new Date(allocationDate) : new Date();
  const tradingName = companyTradingName || '';
  const prefix = `Allocation-${tradingName}`;
  const filename = datedFilename(prefix, date);

  downloadFile(response.data, filename);

  return response.data;
}

// Download EIS1 certificate function
async function downloadEIS1Certificate(params: { id: string }) {
  const response = await api.get(`allocations/${params.id}`, {
    params: {
      isExportToCustodian: true,
    },
    responseType: 'blob',
    validateStatus: (status) => status === 200,
  });

  downloadFile(response.data, `EIS1-Data-${Date.now()}.xlsx`);

  return response.data;
}

export function useDownloadTemplateFile() {
  const notification = useNotification();
  return useMutation<
    Blob,
    AxiosError<ApiResponse<unknown>>,
    Api.DownloadTemplateFormData
  >({
    mutationFn: downloadTemplateFile,
    onError: async (error) => {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          notification.error(
            errorData.responseMsg || 'Failed to download template',
          );
        } catch {
          notification.error('Failed to download template');
        }
      } else {
        notification.error('Failed to download template');
      }
    },
  });
}

export function useUploadAllocationsFile() {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<Api.ProcessShareAllocationsResponse>,
    AxiosError<ApiResponse<unknown>>,
    FormData
  >({
    mutationFn: uploadAllocationsFile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tranches'] });
    },
    onError: async (error) => {
      try {
        notification.error(
          error.response?.data.responseMsg || 'Failed to upload allocations',
        );
      } catch {
        notification.error('Failed to upload allocations');
      }
    },
  });
}

export function useExportToCustodian() {
  const notification = useNotification();
  return useMutation<
    any,
    AxiosError<ApiResponse<unknown>>,
    {
      shareAllocationUploadId: string;
      companyTradingName?: string;
      allocationDate?: Date | string;
    }
  >({
    mutationFn: exportToCustodian,
    onError: async (error) => {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          notification.error(
            errorData.responseMsg || 'Failed to export to custodian',
          );
        } catch {
          notification.error('Failed to export to custodian');
        }
      } else {
        notification.error('Failed to export to custodian');
      }
    },
  });
}

export function useDownloadEIS1Certificate() {
  const notification = useNotification();
  return useMutation<any, AxiosError<ApiResponse<unknown>>, { id: string }>({
    mutationFn: downloadEIS1Certificate,
    onError: async (error) => {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          notification.error(
            errorData.responseMsg || 'Failed to download EIS1 certificate',
          );
        } catch {
          notification.error('Failed to download EIS1 certificate');
        }
      } else {
        notification.error('Failed to download EIS1 certificate');
      }
    },
  });
}

export function useUploadAllocations() {
  return {
    downloadTemplate: useDownloadTemplateFile(),
    uploadFile: useUploadAllocationsFile(),
    exportToCustodian: useExportToCustodian(),
    downloadEIS1Certificate: useDownloadEIS1Certificate(),
  };
}
